import React, { useState, useEffect } from 'react';
import { Package, Search, X, CheckCircle, Calendar, User, Building2, FileText, Plus, Trash2, Save, Link, Eye, Download, Box } from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

export default function GoodsReceiptWithAPI() {
  const [grHeader, setGrHeader] = useState({
    grNo: '',
    grDate: new Date().toISOString().split('T')[0],
    poId: null,
    poNumber: '',
    vendorId: null,
    vendorName: '',
    receivedBy: '',
    receivedById: null,
    remarks: '',
    status: 1,
    siNumber: '',
    drNumber: '',

  });
  const [documentType, setDocumentType] = useState('IS'); // 'IS' or 'DR'
  const [grItems, setGrItems] = useState([
    {
      id: Date.now(),
      poDetailId: null,
      itemCode: '',
      itemDescription: '',
      orderedQty: 0,
      receivedQty: 0,
      uom: 'pcs',
      batchNo: '',
      expiryDate: '',
      warehouseCode: '',
      warehouseId: null,
      remarks: ''
    }
  ]);

  const [savedGRs, setSavedGRs] = useState([]);

  // Modal states
  const [showPOModal, setShowPOModal] = useState(false);
  const [showGRListModal, setShowGRListModal] = useState(false);
  const [selectedGRForView, setSelectedGRForView] = useState(null);
  const [approvedPOs, setApprovedPOs] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [searchPO, setSearchPO] = useState('');
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reference data
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Fetch all reference data on mount
  useEffect(() => {
    fetchVendors();
    fetchUsers();
    fetchWarehouses();
    fetchSavedGRs();
  }, []);

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Users`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/WareHouse`);
      const data = await res.json();
      const warehouseList = Array.isArray(data) ? data : [];
      setWarehouses(warehouseList);

      if (warehouseList.length > 0 && grItems.length > 0 && !grItems[0].warehouseCode) {
        setGrItems([{
          ...grItems[0],
          warehouseCode: warehouseList[0].wareHCode,
          warehouseId: warehouseList[0].id
        }]);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchSavedGRs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header`);
      const data = await res.json();
      setSavedGRs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching GRs:', error);
    }
  };

  // ✅ Fetch approved POs with item details and filter out fully used ones
  const fetchApprovedPOs = async () => {
    setLoadingPOs(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);
      const data = await res.json();

      // Filter only approved POs (status = 2)
      const approved = data.filter(po => po.status === 2);

      // ✅ Fetch PO Details for each PO and check if any items are still available
      const posWithAvailableItems = [];

      for (const po of approved) {
        try {
          const resDetails = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${po.id}`); const poDetails = await resDetails.json();
          const details = Array.isArray(poDetails) ? poDetails : [poDetails];

          // ✅ Check if PO has ANY items where Qty > TotalReceivedQty (items still available)
          const hasAvailableItems = details.some(item => {
            const qty = item.qty || 0;
            const totalReceivedQty = item.totalReceivedQty || 0;
            return qty > totalReceivedQty; // Still has remaining quantity
          });

          if (hasAvailableItems) {
            posWithAvailableItems.push({
              ...po,
              poDetails: details // Attach details to PO for display
            });
          }
        } catch (error) {
          console.error(`Error fetching details for PO ${po.id}:`, error);
        }
      }

      setApprovedPOs(posWithAvailableItems);
      setFilteredPOs(posWithAvailableItems);
    } catch (error) {
      console.error('Error fetching POs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load Purchase Orders',
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoadingPOs(false);
    }
  };

  // Search POs
  useEffect(() => {
    if (searchPO.trim() === '') {
      setFilteredPOs(approvedPOs);
    } else {
      const filtered = approvedPOs.filter(po =>
        po.poNumber.toLowerCase().includes(searchPO.toLowerCase()) ||
        po.vendorId?.toString().includes(searchPO)
      );
      setFilteredPOs(filtered);
    }
  }, [searchPO, approvedPOs]);

  const handleConnectPO = () => {
    fetchApprovedPOs();
    setShowPOModal(true);
  };

  // ✅ Select PO and filter only available items (Qty > TotalReceivedQty)
  const handleSelectPO = async (po) => {
    try {
      const resDetails = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${po.id}`);
      const poDetails = await resDetails.json();
      const details = Array.isArray(poDetails) ? poDetails : [poDetails];

      // ✅ Filter only items where Qty > TotalReceivedQty
      const availableItems = details.filter(item => {
        const qty = item.qty || 0;
        const totalReceivedQty = item.totalReceivedQty || 0;
        return qty > totalReceivedQty;
      });

      if (availableItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Available Items',
          text: 'All items in this PO have been fully received.',
          confirmButtonColor: '#f59e0b'
        });
        return;
      }

      // Fetch vendor name
      let vendorName = po.vendorName || `Vendor ${po.vendorId}`;
      try {
        const resVendor = await fetch(`${API_BASE_URL}api/Vendor/${po.vendorId}`);
        if (resVendor.ok) {
          const vendorData = await resVendor.json();
          vendorName = vendorData.VendorName || vendorData.vendorName || vendorName;
        }
      } catch (vendorError) {
        console.error('Error fetching vendor:', vendorError);
      }

      const newGRNo = generateGRNo();
      setGrHeader({
        grNo: newGRNo,
        grDate: new Date().toISOString().split('T')[0],
        poId: po.id,
        poNumber: po.poNumber,
        vendorId: po.vendorId,       // ✅ int FK
        vendorName: vendorName,      // ✅ real name
        receivedBy: grHeader.receivedBy,
        receivedById: grHeader.receivedById,
        remarks: `From PO: ${po.poNumber}`,
        status: 1,
        siNumber: '',                // ✅ reset
        drNumber: '',                // ✅ reset
      });

      // Map available items to GR items
      const defaultWarehouse = warehouses.length > 0 ? warehouses[0] : null;
      const mappedItems = availableItems.map((item, index) => ({
        id: Date.now() + index,
        poDetailId: item.id,
        itemCode: item.itemCode || `ITEM-${item.id}`,
        itemDescription: item.itemDescription,
        orderedQty: item.qty,
        totalReceivedQty: item.totalReceivedQty || 0, // ✅ Add TotalReceivedQty
        remainingQty: (item.qty || 0) - (item.totalReceivedQty || 0), // ✅ Calculate remaining
        receivedQty: 0,
        uom: item.uom,
        batchNo: '',
        expiryDate: '',
        warehouseCode: defaultWarehouse?.wareHCode || '',
        warehouseId: defaultWarehouse?.id || null,
        remarks: ''
      }));

      setGrItems(mappedItems);
      setShowPOModal(false);

      Swal.fire({
        icon: 'success',
        title: 'PO Connected!',
        html: `<strong>${po.poNumber}</strong> has been connected successfully!<br><small>📦 ${mappedItems.length} available items loaded</small>`,
        confirmButtonColor: '#10b981',
        timer: 3000
      });
    } catch (error) {
      console.error('Error fetching PO details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load PO details',
        confirmButtonColor: '#d33'
      });
    }
  };

  const generateGRNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const seq = String(savedGRs.length + 1).padStart(3, '0');

    return `GR${year}${month}${day}${hours}${minutes}${seconds}-${seq}`;
  };

  const addItem = () => {
    const defaultWarehouse = warehouses.length > 0 ? warehouses[0] : null;
    setGrItems([
      ...grItems,
      {
        id: Date.now(),
        poDetailId: null,
        itemCode: '',
        itemDescription: '',
        orderedQty: 0,
        receivedQty: 0,
        uom: 'pcs',
        batchNo: '',
        expiryDate: '',
        warehouseCode: defaultWarehouse?.wareHCode || '',
        warehouseId: defaultWarehouse?.id || null,
        remarks: ''
      }
    ]);
  };

  const removeItem = (id) => {
    if (grItems.length > 1) {
      setGrItems(grItems.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setGrItems(grItems.map(item => {
      if (item.id === id) {
        if (field === 'receivedQty') {
          const maxQty = item.remainingQty !== undefined ? item.remainingQty : item.orderedQty;
          if (parseFloat(value) > maxQty) {
            Swal.fire({
              icon: 'warning',
              title: 'Exceeded Remaining Qty!',
              text: `Max allowed is ${maxQty}. You entered ${value}.`,
              timer: 2000,
              showConfirmButton: false
            });
            return item;
          }
        }

        const updatedItem = { ...item, [field]: value };

        if (field === 'warehouseCode') {
          const warehouse = warehouses.find(w => w.wareHCode === value);
          updatedItem.warehouseId = warehouse?.id || null;
        }

        return updatedItem;
      }
      return item;
    }));
  };

  // ✅ SIMPLIFIED VERSION - Uses existing PUT endpoint
  const saveGR = async () => {
    // Validation
    if (!grHeader.vendorName) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Vendor is required!',
        confirmButtonColor: '#d33'
      });
      return;
    }
    if (!grHeader.receivedBy) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Received By is required!',
        confirmButtonColor: '#d33'
      });
      return;
    }

    const hasEmptyItems = grItems.some(item =>
      !item.itemCode || !item.itemDescription || item.receivedQty <= 0
    );

    if (hasEmptyItems) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'All items must have Item Code, Description, and Received Qty > 0',
        confirmButtonColor: '#d33'
      });
      return;
    }

    setLoading(true);

    const finalGRNo = grHeader.grNo || generateGRNo();
    const currentUserId = localStorage.getItem('userId') || 1;

    const grData = {
      grNo: finalGRNo,
      grDate: new Date(grHeader.grDate).toISOString(),
      poId: grHeader.poId,
      poNumber: grHeader.poNumber,
      vendorId: grHeader.vendorId,
      vendorName: grHeader.vendorName,
      receivedBy: grHeader.receivedBy,
      receivedById: grHeader.receivedById,
      remarks: grHeader.remarks || '',
      status: 1,
      createdBy: parseInt(currentUserId),

      isallowed_SI: documentType === 'IS',                                        // ← BAGO
      isallowed_DR: documentType === 'DR',                                        // ← BAGO
      sI_number: documentType === 'IS' ? grHeader.siNumber : null,               // ← BAGO
      dR_number: documentType === 'DR' ? grHeader.drNumber : null,               // ← BAGO
      grDetails: grItems.map(item => ({
        poDetailId: item.poDetailId,
        itemCode: item.itemCode,
        itemDescription: item.itemDescription,
        orderedQty: parseFloat(item.orderedQty) || 0,
        receivedQty: parseFloat(item.receivedQty),
        uom: item.uom || 'pcs',
        batchNo: item.batchNo || '',
        expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString() : null,
        warehouseCode: item.warehouseCode || '',
        warehouseId: item.warehouseId,
        remarks: item.remarks || ''
      }))
    };

    try {
      // ============================================
      // Step 1: Save GR Header and Details
      // ============================================
      const response = await fetch(`${API_BASE_URL}api/GR_Header`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(grData)
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to save GR';

        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          if (errorData.errors) {
            const errorMessages = Object.entries(errorData.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n');
            errorMessage = errorMessages;
          } else {
            errorMessage = errorData.message || errorData.title || 'Unknown error';
          }
        } else {
          const errorText = await response.text();
          errorMessage = errorText;
        }

        throw new Error(errorMessage);
      }

      const savedGR = await response.json();
      console.log('✅ GR SAVED:', savedGR);


      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "CREATE",
          module: "Goods Receipt",
          description: `GR ${finalGRNo} created. Vendor: ${grHeader.vendorName}. Items: ${grItems.length}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));
      // ============================================
      // Step 2: Update PO TotalReceivedQty
      // ============================================
      let updatedPOCount = 0;

      if (grHeader.poId) {
        for (const item of grItems) {
          if (item.poDetailId && item.receivedQty > 0) {
            try {
              // Fetch current PO Detail
              const resDetail = await fetch(`${API_BASE_URL}api/T_Po_Details/Id/${item.poDetailId}`);
              const currentDetail = await resDetail.json();

              // Calculate new TotalReceivedQty
              const newTotalReceivedQty = (currentDetail.totalReceivedQty || 0) + parseInt(item.receivedQty);

              // Update using existing PUT endpoint
              const updatePayload = {
                ...currentDetail,
                totalReceivedQty: newTotalReceivedQty
              };

              const updateResponse = await fetch(`${API_BASE_URL}api/T_Po_Details/${item.poDetailId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatePayload)
              });

              if (updateResponse.ok) {
                updatedPOCount++;
                console.log(`✅ Updated PO Detail ${item.poDetailId}: TotalReceivedQty = ${newTotalReceivedQty}`);
              }
            } catch (updateError) {
              console.error(`❌ Error updating PO Detail ${item.poDetailId}:`, updateError);
            }
          }
        }
      }

      // ============================================
      // 🔥 Step 3: Update InventoryData
      // ============================================
      // ============================================
      // 🔥 Step 3: Update InventoryData
      // ============================================
      // ============================================
      // 🔥 Step 3: Update InventoryData (CHECK FIRST)
      // ============================================
      let inventoryUpdatedCount = 0;
      let inventoryCreatedCount = 0;

      for (const item of grItems) {
        try {
          const batchNo = item.batchNo?.trim() || ''; // ✅ Add .trim() here na

          // 🔥 FETCH ALL INVENTORY FOR THIS ITEM + WAREHOUSE
          // 🔥 UPDATE YUNG FETCH URL:
          const checkRes = await fetch(
            `${API_BASE_URL}api/InventoryData/search?itemCode=${encodeURIComponent(item.itemCode)}&warehouseCode=${encodeURIComponent(item.warehouseCode)}`
          );

          let existingRecord = null; // ✅ DECLARE DITO!

          if (checkRes.ok) {
            const existing = await checkRes.json();
            const inventoryList = Array.isArray(existing) ? existing : [existing];

            // 🔥 STRICT MATCH: ItemCode + WarehouseCode + BatchNo
            existingRecord = inventoryList.find(inv => {
              const invBatch = (inv.batchNo || '').trim();
              return invBatch === batchNo;
            });

            console.log(`🔍 Batch [${batchNo || 'NO BATCH'}]:`, existingRecord ? 'FOUND' : 'NOT FOUND');
          }

          if (existingRecord) {
            // 🔥 UPDATE EXISTING BATCH
            const updatePayload = {
              ...existingRecord,
              quantity: (existingRecord.quantity || 0) + parseFloat(item.receivedQty),
              availableQty: (existingRecord.availableQty || 0) + parseFloat(item.receivedQty),
              remarks: `From GR: ${finalGRNo}` // ← PALITAN LAHAT, huwag i-append!
            };


            const putRes = await fetch(`${API_BASE_URL}api/InventoryData/${existingRecord.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatePayload)
            });

            if (putRes.ok) {
              inventoryUpdatedCount++;
              console.log(`✅ Updated inventory ${existingRecord.id}: +${item.receivedQty} (Batch: ${batchNo || 'NONE'})`);
            }
          } else {
            // 🔥 CREATE NEW BATCH
            const inventoryPayload = {
              itemCode: item.itemCode,
              itemDescription: item.itemDescription,
              warehouseCode: item.warehouseCode,
              warehouseId: item.warehouseId,
              batchNo: batchNo,
              expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString() : null,
              quantity: parseFloat(item.receivedQty),
              availableQty: parseFloat(item.receivedQty),
              reservedQty: 0,
              uom: item.uom,
              remarks: `From GR: ${finalGRNo}`,
              poNumber: grHeader.poNumber || null
            };

            const postRes = await fetch(`${API_BASE_URL}api/InventoryData`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(inventoryPayload)
            });

            if (postRes.status === 201 || postRes.ok) {
              inventoryCreatedCount++;
              console.log(`✅ Created inventory for ${item.itemCode} (Batch: ${batchNo || 'NONE'})`);
            }
          }
        } catch (invError) {
          console.error(`❌ Error updating inventory for ${item.itemCode}:`, invError);
        }
      }
      // ============================================
      // Step 4: Success Message
      // ============================================
      const inventoryMessage = inventoryCreatedCount > 0 || inventoryUpdatedCount > 0
        ? `✅ Inventory updated (${inventoryCreatedCount} created, ${inventoryUpdatedCount} updated)<br>`
        : '';

      const poMessage = grHeader.poId
        ? `✅ PO quantities updated (${updatedPOCount}/${grItems.length} items)<br>`
        : '';

      Swal.fire({
        icon: 'success',
        title: 'GR Saved!',
        html: `
        <strong>${finalGRNo}</strong> has been saved successfully!<br>
        ${inventoryMessage}
        ${poMessage}
      `,
        confirmButtonColor: '#10b981',
        timer: 3500
      });

      fetchSavedGRs();
      resetForm();

    } catch (error) {
      console.error('=== ERROR ===', error);
      Swal.fire({
        icon: 'error',
        title: 'Error Saving GR',
        html: `<pre style="text-align: left; max-height: 300px; overflow: auto;">${error.message}</pre>`,
        confirmButtonColor: '#d33',
        width: '600px'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const defaultWarehouse = warehouses.length > 0 ? warehouses[0] : null;
    setDocumentType('IS');

    setGrHeader({
      grNo: '',
      grDate: new Date().toISOString().split('T')[0],
      poId: null,
      poNumber: '',
      vendorId: null,
      vendorName: '',
      receivedBy: '',
      receivedById: null,
      remarks: '',
      status: 1,
      siNumber: '',
      drNumber: '',
    });

    setGrItems([
      {
        id: Date.now(),
        poDetailId: null,
        itemCode: '',
        itemDescription: '',
        orderedQty: 0,
        receivedQty: 0,
        uom: 'pcs',
        batchNo: '',
        expiryDate: '',
        warehouseCode: defaultWarehouse?.wareHCode || '',
        warehouseId: defaultWarehouse?.id || null,
        remarks: ''
      }
    ]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.VendorName || vendor?.vendorName || `Vendor ID: ${vendorId}`;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : `User ID: ${userId}`;
  };

  const handleViewGR = async (grId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header/${grId}`);
      const data = await res.json();
      setSelectedGRForView(data);
    } catch (error) {
      console.error('Error fetching GR details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load GR details',
        confirmButtonColor: '#d33'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-indigo-900 mb-2">📦 Goods Receipt (GR)</h1>
              <p className="text-gray-600">Receive items from approved POs and update inventory stock</p>
            </div>
            <button
              onClick={() => setShowGRListModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
            >
              <Eye className="w-5 h-5" />
              View All GRs ({savedGRs.length})
            </button>
          </div>
        </div>

        {/* GR Form */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">GR Header</h2>
            <button
              onClick={handleConnectPO}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
            >
              <Link className="w-5 h-5" />
              Connect to PO
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">GR No</label>
              <input
                type="text"
                value={grHeader.grNo}
                readOnly
                placeholder="Auto-generated"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">GR Date *</label>
              <input
                type="date"
                value={grHeader.grDate}
                onChange={(e) => setGrHeader({ ...grHeader, grDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Connected PO</label>
              <input
                type="text"
                value={grHeader.poNumber || 'Not connected'}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Vendor *</label>
              <input
                type="text"
                value={grHeader.vendorName}
                onChange={(e) => setGrHeader({ ...grHeader, vendorName: e.target.value })}
                placeholder="Vendor Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                readOnly={grHeader.poId !== null}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Received By *</label>
              <input
                type="text"
                list="users-list"
                value={grHeader.receivedBy}
                onChange={(e) => {
                  const selectedUser = users.find(u => `${u.firstName} ${u.lastName}` === e.target.value);
                  setGrHeader({
                    ...grHeader,
                    receivedBy: e.target.value,
                    receivedById: selectedUser?.id || null
                  });
                }}
                placeholder="Select or type name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <datalist id="users-list">
                {users.map(user => (
                  <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                    {user.positionId} - {user.username}
                  </option>
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Remarks</label>
              <input
                type="text"
                value={grHeader.remarks}
                onChange={(e) => setGrHeader({ ...grHeader, remarks: e.target.value })}
                placeholder="Optional notes"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {/* Document Type Toggle */}
          <div className="col-span-full">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Document Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDocumentType('IS')}
                className={`px-6 py-2 rounded-lg font-bold transition border-2 ${documentType === 'IS'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                  }`}
              >
                SI — Sale Invoice
              </button>
              <button
                type="button"
                onClick={() => setDocumentType('DR')}
                className={`px-6 py-2 rounded-lg font-bold transition border-2 ${documentType === 'DR'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                  }`}
              >
                DR — Delivery Receipt
              </button>
            </div>
          </div>

          {/* SI Number — show only if IS */}
          {documentType === 'IS' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">SI Number</label>
              <input
                type="text"
                value={grHeader.siNumber}
                onChange={(e) => setGrHeader({ ...grHeader, siNumber: e.target.value })}
                placeholder="Enter SI Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* DR Number — show only if DR */}
          {documentType === 'DR' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">DR Number</label>
              <input
                type="text"
                value={grHeader.drNumber}
                onChange={(e) => setGrHeader({ ...grHeader, drNumber: e.target.value })}
                placeholder="Enter DR Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
        </div>

        {/* GR Items Table */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">GR Items</h2>
            {/* <button
              onClick={addItem}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Item
            </button> */}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-indigo-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">#</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Item Code *</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Description *</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-bold">Ordered Qty</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-bold bg-blue-50">Total Received</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-bold">Received Qty *</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">UOM</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Batch/Lot</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Expiry</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Warehouse</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {grItems.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-3 py-2 text-center">{index + 1}</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="text"
                        value={item.itemCode}
                        onChange={(e) => updateItem(item.id, 'itemCode', e.target.value)}
                        placeholder="ITEM001"
                        readOnly={item.poDetailId !== null}
                        className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-indigo-500 ${item.poDetailId !== null ? 'bg-gray-50' : ''
                          }`}
                      />
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="text"
                        value={item.itemDescription}
                        onChange={(e) => updateItem(item.id, 'itemDescription', e.target.value)}
                        placeholder="Item description"
                        readOnly={item.poDetailId !== null}
                        className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-indigo-500 ${item.poDetailId !== null ? 'bg-gray-50' : ''
                          }`}
                      />
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="number"
                        value={item.orderedQty}
                        readOnly
                        className="w-20 px-2 py-1 border rounded bg-yellow-50 text-center font-semibold"
                      />
                    </td>
                    {/* ✅ NEW: Total Received Qty Column with Blue Color */}
                    <td className="border border-gray-300 px-3 py-2 bg-blue-50">
                      <input
                        type="number"
                        value={item.totalReceivedQty || 0}
                        readOnly
                        className="w-20 px-2 py-1 border rounded bg-blue-100 text-center font-bold text-blue-700"
                      />
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="number"
                        value={item.receivedQty}
                        onChange={(e) => updateItem(item.id, 'receivedQty', e.target.value)}
                        min="0"
                        max={item.remainingQty || item.orderedQty || undefined}
                        className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-green-500 text-center font-semibold"
                      />
                      {item.remainingQty !== undefined && (
                        <div className="text-xs text-blue-600 mt-1">
                          Max: {item.remainingQty}
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <span className="px-2 py-1 text-sm font-semibold text-gray-800">
                        {item.uom || "—"}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="text"
                        value={item.batchNo}
                        onChange={(e) => updateItem(item.id, 'batchNo', e.target.value)}
                        placeholder="A1"
                        className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="date"
                        value={item.expiryDate}
                        onChange={(e) => updateItem(item.id, 'expiryDate', e.target.value)}
                        className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <select
                        value={item.warehouseCode}
                        onChange={(e) => updateItem(item.id, 'warehouseCode', e.target.value)}
                        className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-indigo-500"
                      >
                        {warehouses.length > 0 ? (
                          warehouses.map(wh => (
                            <option key={wh.id} value={wh.wareHCode}>
                              {wh.wareHouseName} ({wh.wareHCode})
                            </option>
                          ))
                        ) : (
                          <option value="">No warehouses available</option>
                        )}
                      </select>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <button
                        onClick={() => removeItem(item.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded transition flex items-center gap-1 mx-auto"
                        disabled={grItems.length === 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={saveGR}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg font-bold text-lg transition shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save GR
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* PO Selection Modal */}
      {showPOModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-600 text-white px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6" />
                  <div>
                    <h3 className="text-xl font-bold">Select Approved PO (With Available Items)</h3>
                    <p className="text-indigo-100 text-sm">Only showing POs with items not fully received</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPOModal(false)}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-2 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 border-b bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by PO Number or Vendor ID..."
                  value={searchPO}
                  onChange={(e) => setSearchPO(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingPOs ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mb-4"></div>
                    <p className="text-gray-600">Loading approved POs...</p>
                  </div>
                </div>
              ) : filteredPOs.length === 0 ? (
                <div className="text-center py-20">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-semibold">No available POs found</p>
                  <p className="text-gray-400 text-sm mt-2">All items have been fully received or no approved POs exist</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPOs.map((po) => {
                    const availableItems = po.poDetails?.filter(item =>
                      (item.qty || 0) > (item.totalReceivedQty || 0)
                    ) || [];

                    return (
                      <div
                        key={po.id}
                        className="border-2 border-gray-200 hover:border-indigo-500 rounded-xl p-5 bg-white hover:shadow-xl transition-all cursor-pointer"
                        onClick={() => handleSelectPO(po)}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-6 h-6 text-indigo-600" />
                              <h4 className="font-bold text-xl text-indigo-900">{po.poNumber}</h4>
                              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                ✓ Approved
                              </span>
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                {availableItems.length} items available
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700">
                                  <strong>Vendor:</strong> {getVendorName(po.vendorId)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700">
                                  <strong>Delivery:</strong> {formatDate(po.deliveryDate)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700">
                                  <strong>Prepared By:</strong> {getUserName(po.preparedBy)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700">
                                  <strong>Created:</strong> {formatDate(po.poCreatedDate)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button className="ml-4 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md hover:shadow-lg">
                            Select PO
                          </button>
                        </div>

                        {/* Item Details Table */}
                        <div className="mt-4 border-t pt-4">
                          <h5 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                            <Box className="w-4 h-4" />
                            Available Items:
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-indigo-50">
                                  <th className="border border-gray-300 px-2 py-1 text-left font-semibold">#</th>
                                  <th className="border border-gray-300 px-2 py-1 text-left font-semibold">Item Code</th>
                                  <th className="border border-gray-300 px-2 py-1 text-left font-semibold">Description</th>
                                  <th className="border border-gray-300 px-2 py-1 text-center font-semibold">Ordered</th>
                                  <th className="border border-gray-300 px-2 py-1 text-center font-semibold bg-blue-50">Total Received</th>
                                  <th className="border border-gray-300 px-2 py-1 text-center font-semibold">Remaining</th>
                                  <th className="border border-gray-300 px-2 py-1 text-center font-semibold">UOM</th>
                                </tr>
                              </thead>
                              <tbody>
                                {availableItems.map((item, idx) => {
                                  const qty = item.qty || 0;
                                  const totalReceivedQty = item.totalReceivedQty || 0;
                                  const remaining = qty - totalReceivedQty;

                                  return (
                                    <tr key={item.id} className="hover:bg-indigo-50">
                                      <td className="border border-gray-300 px-2 py-1 text-center">{idx + 1}</td>
                                      <td className="border border-gray-300 px-2 py-1 font-mono text-xs">{item.itemCode || `ITEM-${item.id}`}</td>
                                      <td className="border border-gray-300 px-2 py-1">{item.itemDescription || 'N/A'}</td>
                                      <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{qty}</td>
                                      <td className="border border-gray-300 px-2 py-1 text-center text-blue-700 font-bold bg-blue-50">{totalReceivedQty}</td>
                                      <td className="border border-gray-300 px-2 py-1 text-center text-green-600 font-bold">{remaining}</td>
                                      <td className="border border-gray-300 px-2 py-1 text-center text-xs">{item.uom || 'pcs'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {po.remarks && (
                          <p className="text-xs text-gray-600 mt-3 italic bg-amber-50 p-2 rounded border-l-2 border-amber-400">
                            💬 {po.remarks}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GR List Modal */}
      {showGRListModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6" />
                  <div>
                    <h3 className="text-xl font-bold">Saved Goods Receipts</h3>
                    <p className="text-blue-100 text-sm">{savedGRs.length} GRs in the system</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowGRListModal(false)}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-2 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {savedGRs.length === 0 ? (
                <div className="text-center py-20">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No GRs saved yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedGRs.map((gr) => {
                    const isReverted = gr.status === -2;
                    const isApproved = gr.status === 2;
                    const isClosed = gr.status === 3;

                    // Card bg color per status
                    const cardBg = isReverted
                      ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-300'
                      : isApproved
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
                        : isClosed
                          ? 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300'
                          : 'bg-gradient-to-r from-green-50 to-emerald-50 border-gray-200';

                    // Status badge
                    const statusBadge = isReverted ? (
                      <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-1">
                        <X className="w-4 h-4" />
                        Reverted
                      </span>
                    ) : isApproved ? (
                      <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Approved
                      </span>
                    ) : isClosed ? (
                      <span className="bg-gray-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Closed
                      </span>
                    ) : (
                      <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Active
                      </span>
                    );

                    return (
                      <div
                        key={gr.id}
                        className={`border-2 rounded-lg p-4 hover:shadow-lg transition ${cardBg}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-lg text-indigo-900">{gr.grNo}</h4>
                              {isReverted && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold border border-orange-300">
                                  ⚠️ This GR has been reverted
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {formatDate(gr.grDate)} | {gr.vendorName} | Received by: {gr.receivedBy}
                            </p>
                            {gr.poNumber && (
                              <p className="text-xs text-indigo-700 font-semibold mt-1">
                                🔗 Connected to PO: {gr.poNumber}
                              </p>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                              📦 {gr.grDetails?.length || 0} items
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {statusBadge}
                            <button
                              onClick={() => handleViewGR(gr.id)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* GR View Modal */}
      {selectedGRForView && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-600 text-white px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6" />
                  <div>
                    <h3 className="text-xl font-bold">GR Details: {selectedGRForView.grNo}</h3>
                    <p className="text-indigo-100 text-sm">{formatDate(selectedGRForView.grDate)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedGRForView(null)}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-2 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold">Vendor</p>
                  <p className="text-sm font-bold text-gray-900">{selectedGRForView.vendorName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold">Received By</p>
                  <p className="text-sm font-bold text-gray-900">{selectedGRForView.receivedBy}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold">PO Number</p>
                  <p className="text-sm font-bold text-gray-900">{selectedGRForView.poNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedGRForView.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                    {selectedGRForView.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {selectedGRForView.remarks && (
                <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <p className="text-xs text-amber-700 uppercase font-semibold mb-1">Remarks</p>
                  <p className="text-sm text-gray-800">{selectedGRForView.remarks}</p>
                </div>
              )}

              <h4 className="text-lg font-bold text-gray-800 mb-3">Items Received</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-indigo-100">
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">#</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Item Code</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Description</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm font-bold">Received Qty</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">UOM</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Batch</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Warehouse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGRForView.grDetails?.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-3 py-2 text-center">{index + 1}</td>
                        <td className="border border-gray-300 px-3 py-2 font-semibold">{item.itemCode}</td>
                        <td className="border border-gray-300 px-3 py-2">{item.itemDescription}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center font-bold text-green-700">{item.receivedQty}</td>
                        <td className="border border-gray-300 px-3 py-2">{item.uom}</td>
                        <td className="border border-gray-300 px-3 py-2">{item.batchNo || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{item.warehouseCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}